import * as React from 'react'
import { GridPrivateApiCommunity } from '../../../models/api/gridApiCommunity'
import {
  GridEditingApi,
  GridEditingPrivateApi,
  GridEditingSharedApi,
  GridEditingSharedPrivateApi,
} from '../../../models/api/gridEditingApi'
import { DataGridProcessedProps } from '../../../models/props/DataGridProps'
import { useGridApiMethod } from '../../utils/useGridApiMethod'
import { ReactTableRowId } from '../../../models/gridReactTable'
import { useGridCellEditing } from './useGridCellEditing'
import {
  GridCellModes,
  GridEditCellProps,
  GridEditModes,
  GridEditingState,
} from '../../../models/gridEditRowModel'
import { useGridRowEditing } from './useGridRowEditing'
import { GridStateInitializer } from '../../utils/useGridInitializeState'
import { gridEditRowsStateSelector } from './gridEditingSelectors'
import { useEventCallback } from '@applift/factor'

export const editingStateInitializer: GridStateInitializer = state => ({
  ...state,
  editRows: {},
})

export const useGridEditing = (
  apiRef: React.MutableRefObject<GridPrivateApiCommunity>,
  props: Pick<
    DataGridProcessedProps,
    'isCellEditable' | 'editMode' | 'processRowUpdate' | 'onSetEditCellValue'
  >
) => {
  useGridCellEditing(apiRef, props)
  useGridRowEditing(apiRef, props)

  const debounceMap = React.useRef<
    Record<ReactTableRowId, Record<string, [NodeJS.Timeout, () => void]>>
  >({})

  const {
    isCellEditable: isCellEditableProp,
    onSetEditCellValue: onSetEditCellValueProp,
  } = props

  const onSetEditCellValue = useEventCallback<
    [GridEditCellProps | undefined, boolean, ReactTableRowId, string],
    void
  >((state, hasError, rowId, field) => {
    if (onSetEditCellValueProp) {
      onSetEditCellValueProp(state, hasError, rowId, field)
    }
  })

  const isCellEditable = React.useCallback<GridEditingApi['isCellEditable']>(
    params => {
      const column = apiRef.current.tableInstance.getColumn(params.field)

      if (!(column?.columnDef.meta?.editable ?? false)) {
        return false
      }

      if (!column?.columnDef?.meta?.renderEditCell) {
        return false
      }
      if (isCellEditableProp) {
        return isCellEditableProp(params)
      }
      return true
    },
    [isCellEditableProp, apiRef]
  )

  const maybeDebounce = (
    rowId: ReactTableRowId,
    field: string,
    debounceMs: number | undefined,
    callback: () => Promise<void>
  ) => {
    if (!debounceMs) {
      callback()
      return
    }

    if (!debounceMap.current[rowId]) {
      debounceMap.current[rowId] = {}
    }

    if (debounceMap.current![rowId]![field]) {
      const [timeout] = debounceMap.current![rowId]![field]!
      clearTimeout(timeout)
    }

    // To run the callback immediatelly without waiting the timeout
    const runImmediately = () => {
      const [timeout] = debounceMap.current![rowId]![field]!
      clearTimeout(timeout)
      callback()
      delete debounceMap.current![rowId]![field]
    }

    const timeout = setTimeout(() => {
      callback()
      delete debounceMap.current![rowId]![field]
    }, debounceMs)

    debounceMap.current![rowId]![field] = [timeout, runImmediately]
  }

  React.useEffect(() => {
    const debounces = debounceMap.current
    // run cleanup
    return () => {
      Object.entries(debounces).forEach(([rowId, fields]) => {
        Object.keys(fields).forEach(field => {
          const [timeout] = debounces![rowId]![field]!
          clearTimeout(timeout)
          delete debounces![rowId]![field]
        })
      })
    }
  }, [])

  const runPendingEditCellValueMutation = React.useCallback<
    GridEditingPrivateApi['runPendingEditCellValueMutation']
  >((rowId, field) => {
    if (!debounceMap.current[rowId]) {
      return
    }
    if (!field) {
      Object.keys(debounceMap.current![rowId]!).forEach(debouncedField => {
        const [, runCallback] = debounceMap.current![rowId]![debouncedField]!
        runCallback()
      })
    } else if (debounceMap.current![rowId]![field]) {
      const [, runCallback] = debounceMap.current![rowId]![field]!
      runCallback()
    }
  }, [])

  const setEditCellValue = React.useCallback<
    GridEditingApi['setEditCellValue']
  >(
    params => {
      const { rowId, field, debounceMs } = params

      return new Promise(resolve => {
        maybeDebounce(rowId, field, debounceMs, async () => {
          const setEditCellValueToCall =
            props.editMode === GridEditModes.Row
              ? apiRef.current.setRowEditingEditCellValue
              : apiRef.current.setCellEditingEditCellValue

          // Check if the cell is in edit mode
          // By the time this callback runs the user may have cancelled the editing
          if (apiRef.current.getCellMode(rowId, field) === GridCellModes.Edit) {
            const result = await setEditCellValueToCall(params)
            resolve(result)
            if (onSetEditCellValue) {
              const gridEditingState: GridEditingState =
                apiRef.current.state?.editRows ?? {}
              const state = gridEditingState[rowId]?.[field]
              onSetEditCellValue(state, result, rowId, field)
            }
          }
        })
      })
    },
    [apiRef, props.editMode, onSetEditCellValue]
  )

  const getRowWithUpdatedValues = React.useCallback<
    GridEditingSharedApi['getRowWithUpdatedValues']
  >(
    (id, field) => {
      return props.editMode === GridEditModes.Cell
        ? apiRef.current.getRowWithUpdatedValuesFromCellEditing(id, field)
        : apiRef.current.getRowWithUpdatedValuesFromRowEditing(id)
    },
    [apiRef, props.editMode]
  )

  const getEditCellMeta = React.useCallback<
    GridEditingSharedApi['getEditCellMeta']
  >(
    (id, field) => {
      const editingState = gridEditRowsStateSelector(apiRef.current.state)

      return { changeReason: editingState![id]![field]!.changeReason }
    },
    [apiRef]
  )

  const editingSharedApi: GridEditingSharedApi = {
    isCellEditable,
    setEditCellValue,
    getRowWithUpdatedValues,
    getEditCellMeta,
  }

  const editingSharedPrivateApi: GridEditingSharedPrivateApi = {
    runPendingEditCellValueMutation,
  }

  useGridApiMethod(apiRef, editingSharedApi, 'public')
  useGridApiMethod(apiRef, editingSharedPrivateApi, 'private')
}
